import React from "react";
import HeroLoop from "@/shared/components/Home";
import { Download, MessageCircle, Swords, Package, FileDown, Sparkles, CheckCircle2 } from "lucide-react";
import { useClientDownload } from "@/shared/hooks/useClientDownload";

type Step = {
  num: number;
  icon: React.ReactNode;
  title: string;
  body: React.ReactNode;
};

// Fallback usado so se a config no DB ainda nao tiver sido setada pelo master.
const FALLBACK_DOWNLOAD_URL =
  "https://drive.google.com/file/d/1dmnmJAjY55JNvJK-ssiXb-2agnPZlDjH/view?usp=sharing";

function buildSteps(downloadUrl: string, downloadLabel: string, downloadNotes: string | null): Step[] {
  const versionTag = downloadLabel || "Download Cabal Liberty EP39";
  return [
    {
      num: 1,
      icon: <CheckCircle2 size={14} />,
      title: "Credenciais de Acesso",
      body: (
        <>
          Para receber suas credenciais de acesso, acesse o Discord (discord.gg/n9ccbGhK72), entre na sala{" "}
          <strong>"CHAT-TESTERS"</strong> e digite <span className="cmd">/ACESSO</span> no chat da sala.
          Você receberá seu <strong>"LOGIN"</strong> e <strong>"SENHA"</strong> no privado.
        </>
      ),
    },
    {
      num: 2,
      icon: <FileDown size={14} />,
      title: "Download do Cliente",
      body: (
        <>
          Segue o link para baixar o cliente do jogo:
          <br />
          <a className="link-box" href={downloadUrl} target="_blank" rel="noreferrer">
            <Download size={14} className="link-icon" />
            {versionTag}
          </a>
          <div className="obs-box">
            <strong>OBS:</strong> Se você já participou do BETA e já tem o cliente,
            não precisa baixar novamente — basta <strong>"ATUALIZAR"</strong> pelo <strong>"UPDATE"</strong>.
          </div>
          {downloadNotes && (
            <div className="obs-box" style={{ marginTop: 8 }}>
              {downloadNotes}
            </div>
          )}
        </>
      ),
    },
    {
      num: 3,
      icon: <Package size={14} />,
      title: "Extração dos Arquivos",
      body: <>Após o download, extraia (descompacte) os arquivos para a pasta e diretório de sua preferência.</>,
    },
    {
      num: 4,
      icon: <Swords size={14} />,
      title: "Executar o Jogo",
      body: (
        <>
          Após a atualização, clique com o botão direito no arquivo <strong>"CABAL.EXE"</strong> e
          execute como <strong>"ADMINISTRADOR"</strong>.
        </>
      ),
    },
    {
      num: 5,
      icon: <MessageCircle size={14} />,
      title: "Discord — Suporte Oficial",
      body: (
        <>
          <div className="discord-highlight">
            O uso do <strong>"DISCORD"</strong> é <strong>OBRIGATÓRIO</strong> para suporte ao game.
            O WhatsApp <u>não</u> será utilizado para suporte, a fim de evitar conflito de informações.
          </div>
          <a className="link-box" href="https://discord.gg/n9ccbGhK72" target="_blank" rel="noreferrer">
            <MessageCircle size={14} className="link-icon" />
            discord.gg/n9ccbGhK72 — Clique para acessar
          </a>
        </>
      ),
    },
    {
      num: 6,
      icon: <Sparkles size={14} />,
      title: "Jogar!",
      body: <>Só logar, criar seu personagem conforme suas preferências e partir para a aventura! ⚔️</>,
    },
  ];
}

export default function ConvitePage() {
  const { data: clientDownload } = useClientDownload();
  const downloadUrl = clientDownload?.url || FALLBACK_DOWNLOAD_URL;
  const downloadLabel = clientDownload?.label
    ? `drive — ${clientDownload.label}${clientDownload.version ? ` (v${clientDownload.version})` : ""}`
    : "drive.google.com — Download Cabal Liberty EP39";
  const downloadNotes = clientDownload?.notes || null;
  const STEPS = buildSteps(downloadUrl, downloadLabel, downloadNotes);
  return (
    <div className="relative min-h-screen text-white">
      {/* BG do site (bg.png + drift) */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div
          className="absolute -inset-[3%]"
          style={{
            backgroundImage: "url(/assets/bg.webp)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "grayscale(70%) brightness(0.5) contrast(1.30)",
            animation: "bgDrift 42s ease-in-out infinite",
            willChange: "transform",
          }}
        />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,225,215,0.13),transparent_70%)]" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60" />
      </div>

      {/* Particles do home (HeroLoop ja renderiza apenas particles) */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <HeroLoop />
      </div>

      {/* Vignette */}
      <div className="pointer-events-none fixed inset-0 [box-shadow:inset_0_0_80px_rgba(0,0,0,0.45)] z-40" />

      <div className="relative z-10 invite-wrap-inner">
        <div className="inner mx-auto" style={{ maxWidth: 720, padding: "48px 24px 64px" }}>
          {/* Logo / título */}
          <div className="logo-area">
            <div className="logo-badge">◆ Liberty Games ◆</div>
            <h1 className="main-title">
              CABAL LIBERTY
              <span>Teste Final — EP39</span>
            </h1>
          </div>

          {/* Ornamento */}
          <div className="ornament">
            <div className="ornament-line" />
            <div className="ornament-gem" />
            <div className="ornament-line" />
          </div>

          {/* Intro */}
          <p className="intro-text">
            Olá! Venho por meio deste te convidar a fazer parte da nossa
            <br />
            <strong>EQUIPE CABAL LIBERTY TESTE FINAL EP39</strong> 🚀
            <br />
            <br />
            Antes de tudo, peço que leia este convite com bastante atenção.
            <br />
            Após a leitura, você saberá direitinho como proceder para receber suas credenciais!
          </p>

          {/* Steps */}
          <div className="steps-container">
            {STEPS.map((s) => (
              <div key={s.num} className="step-card">
                <div className="step-header">
                  <span className="step-num">
                    <span className="inline-flex items-center gap-1.5">
                      {s.icon}
                      PASSO {s.num}
                    </span>
                  </span>
                  <div className="step-title">{s.title}</div>
                </div>
                <div className="step-body">{s.body}</div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="footer-block">
            <p className="footer-msg">
              Qualquer dúvida, estou à disposição! 💪
              <br />
              <strong>Abraços e vamos à luta! 🎯🔥</strong>
            </p>
            <div className="footer-sig">
              <span>Cabal Liberty EP39</span> &nbsp;◆&nbsp; Liberty Games Ltda
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700;900&family=Crimson+Pro:ital,wght@0,300;0,400;0,600;1,300&display=swap');

        @keyframes bgDrift {
          0%,100% { transform: translate(0,0); }
          50%     { transform: translate(-1.2%,-0.8%); }
        }

        .invite-wrap-inner {
          font-family: 'Crimson Pro', Georgia, serif;
          color: #c8c0a8;
        }
        .invite-wrap-inner .logo-area { text-align: center; margin-bottom: 32px; }
        .invite-wrap-inner .logo-badge {
          display: inline-block;
          border: 1px solid rgba(0, 225, 215, 0.35);
          border-radius: 4px;
          padding: 6px 20px;
          font-family: 'Cinzel', serif;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 3px;
          color: #00e1d7;
          text-transform: uppercase;
          margin-bottom: 20px;
          background: rgba(0, 225, 215, 0.05);
        }
        .invite-wrap-inner .main-title {
          font-family: 'Cinzel', serif;
          font-size: clamp(28px, 5vw, 44px);
          font-weight: 900;
          color: #ffffff;
          letter-spacing: 2px;
          line-height: 1.1;
          text-shadow: 0 0 40px rgba(0, 225, 215, 0.4);
        }
        .invite-wrap-inner .main-title span {
          color: #00e1d7;
          font-size: 0.62em;
          display: block;
          letter-spacing: 5px;
          margin-top: 8px;
          font-weight: 600;
        }
        .invite-wrap-inner .ornament { display: flex; align-items: center; gap: 12px; margin: 24px 0; }
        .invite-wrap-inner .ornament-line { flex: 1; height: 1px; background: linear-gradient(90deg, transparent, rgba(0,225,215,0.5), transparent); }
        .invite-wrap-inner .ornament-gem { width: 8px; height: 8px; background: #00e1d7; transform: rotate(45deg); opacity: 0.85; box-shadow: 0 0 10px rgba(0,225,215,0.6); }

        .invite-wrap-inner .intro-text {
          font-size: 17px;
          font-weight: 300;
          line-height: 1.75;
          color: #c8d4d8;
          text-align: center;
          margin-bottom: 36px;
          font-style: italic;
          padding: 0 8px;
        }
        .invite-wrap-inner .intro-text strong { color: #00e1d7; font-style: normal; font-weight: 600; }

        .invite-wrap-inner .steps-container { display: flex; flex-direction: column; gap: 0; }
        .invite-wrap-inner .step-card {
          background: rgba(0, 0, 0, 0.75);
          border: 1px solid rgba(0, 225, 215, 0.18);
          backdrop-filter: blur(8px);
          border-radius: 0;
          padding: 20px 24px;
          position: relative;
          transition: background 0.2s, border-color 0.2s;
        }
        .invite-wrap-inner .step-card:first-child { border-radius: 10px 10px 0 0; }
        .invite-wrap-inner .step-card:last-child { border-radius: 0 0 10px 10px; }
        .invite-wrap-inner .step-card + .step-card { border-top: none; }
        .invite-wrap-inner .step-card:hover {
          background: rgba(0, 225, 215, 0.05);
          border-color: rgba(0, 225, 215, 0.35);
        }
        .invite-wrap-inner .step-header { display: flex; align-items: flex-start; gap: 14px; margin-bottom: 10px; }
        .invite-wrap-inner .step-num {
          font-family: 'Cinzel', serif;
          font-size: 11px;
          font-weight: 700;
          color: #00e1d7;
          background: rgba(0, 225, 215, 0.1);
          border: 1px solid rgba(0, 225, 215, 0.3);
          border-radius: 4px;
          padding: 4px 10px;
          letter-spacing: 1px;
          white-space: nowrap;
          margin-top: 1px;
          flex-shrink: 0;
        }
        .invite-wrap-inner .step-title {
          font-family: 'Cinzel', serif;
          font-size: 13px;
          font-weight: 700;
          color: #ffffff;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          line-height: 1.4;
          padding-top: 5px;
        }
        .invite-wrap-inner .step-body {
          font-size: 15px;
          line-height: 1.75;
          color: #b8c0c4;
          padding-left: 0;
        }
        .invite-wrap-inner .step-body strong,
        .invite-wrap-inner .step-body .cmd {
          color: #00e1d7;
          font-weight: 600;
          font-style: normal;
        }
        .invite-wrap-inner .step-body .cmd {
          font-family: 'Cinzel', serif;
          background: rgba(0, 225, 215, 0.1);
          border: 1px solid rgba(0, 225, 215, 0.3);
          border-radius: 3px;
          padding: 1px 8px;
          font-size: 13px;
          letter-spacing: 1px;
        }

        .invite-wrap-inner .link-box {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          margin-top: 10px;
          background: rgba(0, 225, 215, 0.06);
          border: 1px solid rgba(0, 225, 215, 0.3);
          border-radius: 4px;
          padding: 8px 14px;
          font-size: 13px;
          color: #8ae0d8;
          text-decoration: none;
          word-break: break-all;
          transition: background 0.2s, color 0.2s;
          cursor: pointer;
        }
        .invite-wrap-inner .link-box:hover {
          background: rgba(0, 225, 215, 0.14);
          color: #ffffff;
        }
        .invite-wrap-inner .link-icon { color: #00e1d7; flex-shrink: 0; }

        .invite-wrap-inner .obs-box {
          background: rgba(255, 200, 50, 0.05);
          border-left: 2px solid rgba(255, 200, 50, 0.5);
          border-radius: 0 4px 4px 0;
          padding: 10px 14px;
          margin-top: 12px;
          font-size: 14px;
          font-style: italic;
          color: #c8b878;
        }
        .invite-wrap-inner .obs-box strong { color: #ffd968; font-style: normal; }

        .invite-wrap-inner .discord-highlight {
          background: rgba(88, 101, 242, 0.1);
          border: 1px solid rgba(88, 101, 242, 0.35);
          border-radius: 4px;
          padding: 10px 14px;
          margin-top: 4px;
          margin-bottom: 4px;
          font-size: 15px;
          line-height: 1.7;
          color: #b8c0f8;
        }
        .invite-wrap-inner .discord-highlight strong { color: #d8d8ff; }

        .invite-wrap-inner .footer-block {
          text-align: center;
          margin-top: 36px;
          padding-top: 28px;
          border-top: 1px solid rgba(0, 225, 215, 0.18);
        }
        .invite-wrap-inner .footer-msg {
          font-size: 18px;
          font-style: italic;
          color: #b8c0c4;
          line-height: 1.6;
          margin-bottom: 16px;
        }
        .invite-wrap-inner .footer-msg strong { color: #00e1d7; font-style: normal; }
        .invite-wrap-inner .footer-sig {
          font-family: 'Cinzel', serif;
          font-size: 11px;
          letter-spacing: 2px;
          color: #607078;
          text-transform: uppercase;
        }
        .invite-wrap-inner .footer-sig span { color: #8a9aa0; }

        @keyframes fadeIn { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        .invite-wrap-inner .inner > * { animation: fadeIn 0.6s ease both; }
        .invite-wrap-inner .logo-area { animation-delay: 0s; }
        .invite-wrap-inner .ornament { animation-delay: 0.1s; }
        .invite-wrap-inner .intro-text { animation-delay: 0.2s; }
        .invite-wrap-inner .steps-container { animation-delay: 0.3s; }
        .invite-wrap-inner .footer-block { animation-delay: 0.5s; }
      `}</style>
    </div>
  );
}
